import React from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import { RichText } from "prismic-reactjs"
import LocalizedLink from "../components/localizedLink"
import SEO from "../components/SEO"
import Hero from "../components/hero"
import Card from "../components/card"
import Trial from "../components/trial"
import slugify from "slugify"
import { css } from "@emotion/core"
import { colors } from "../styles/styles"

const BlogPage = ({
  data,
  pageContext: { locale, subPages, tag },
  location,
}) => {
  const blogpageData = data.prismic.blogpage
  const allBlogPostsData = data.prismic.allBlogposts
  const allFeaturedBlogposts = data.prismic.allFeaturedBlogposts
  const allBlogpostsByTags = data.prismic.allBlogpostsByTags

  const allTagsInLang = new Set([])
  subPages
    .filter(post => post.node._meta.lang === locale)
    .map(post => post.node._meta.tags.map(tag => allTagsInLang.add(tag)))
  return (
    <>
      <SEO
        title={blogpageData.meta_title}
        desc={blogpageData.meta_description}
        metadata={data.site.siteMetadata}
        locale={locale}
        pathname={location.pathname}
      />
      <Hero
        hero={{
          title: blogpageData.title,
          subtitle: blogpageData.subtitle,
          image: {
            image: blogpageData.main_image,
            sharp: blogpageData.main_imageSharp,
          },
        }}
      />
      <section className="section" css={blogPageCSS}>
        <div className="container">
          <div className="columns">
            <div className="column is-6 blog__main">
              <Card postData={{ node: blogpageData.main_featured_post }} />
            </div>
            <div className="column is-6 blog__sidebar">
              <h3 className="title is-5 has-text-weight-normal is-uppercase">
                {blogpageData.title_featured &&
                  RichText.asText(blogpageData.title_featured)}
              </h3>
              <ol style={{ listStyleType: "none" }}>
                {allFeaturedBlogposts.edges.map((featured, i) => (
                  <li key={i}>
                    <Card postData={featured} layout={"list"} />
                  </li>
                ))}
              </ol>
            </div>
          </div>
          <div className="columns is-multiline is-centered my-6 is-mobile">
            {Array.from(allTagsInLang).map((tag, i) => {
              return (
                <div key={`tag` + i} className="">
                  <LocalizedLink to={`blog/tag/${slugify(tag)}`}>
                    <div className="tags are-medium">
                      <span
                        className={`tag is-rounded has-background-white has-text-primary has-text-weight-medium button__gradient-border mx-4 my-4  ${slugify(
                          tag
                        )}`}
                      >
                        <span className="icon is-size-5 ml-1 mr-2">
                          <i className={`icon-all`}></i>
                        </span>
                        {tag}
                      </span>
                    </div>
                  </LocalizedLink>
                </div>
              )
            })}
          </div>

          {!tag && (
            <div className="columns is-multiline">
              {allBlogPostsData.edges.map((post, i) => (
                <div key={`post` + i} className="column is-4 blog__thumbnail">
                  <Card postData={post} />
                </div>
              ))}
            </div>
          )}

          {tag && (
            <section className="section">
              <h3 className="title is-4">
                <span className="tag is-large">{tag}</span>
              </h3>
              <div className="columns is-multiline">
                {allBlogpostsByTags.edges.map((post, i) => (
                  <div key={`postBytag` + i} className="column is-4">
                    <Card postData={post} />
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </section>
      <Trial trial={data.prismic.allTrialcomponents.edges[0].node} />
    </>
  )
}

export default BlogPage

BlogPage.propTypes = {
  data: PropTypes.shape({
    prismic: PropTypes.object.isRequired,
  }).isRequired,
}

export const BlogPageQuery = graphql`
  query BlogPageQuery($uid: String!, $tag: [String!], $locale: String!) {
    prismic {
      blogpage(uid: $uid, lang: $locale) {
        meta_title
        meta_description
        title
        subtitle
        title_featured
        main_featured_post {
          ... on PRISMIC_Blogpost {
            ...CardPost
          }
        }
        main_image
        main_imageSharp {
          extension
          childImageSharp {
            fluid {
              ...GatsbyImageSharpFluid_tracedSVG
            }
          }
        }
      }

      allBlogposts(lang: $locale, sortBy: meta_firstPublicationDate_DESC) {
        totalCount
        edges {
          node {
            ...CardPost
          }
        }
      }
      allFeaturedBlogposts: allBlogposts(
        lang: $locale
        sortBy: meta_firstPublicationDate_DESC
        last: 3
      ) {
        totalCount
        edges {
          node {
            ...CardPost
          }
        }
      }
      allBlogpostsByTags: allBlogposts(
        lang: $locale
        tags: $tag
        sortBy: meta_firstPublicationDate_DESC
      ) {
        totalCount
        edges {
          node {
            ...CardPost
          }
        }
      }

      ...Trial
    }

    site {
      ...SiteInformation
    }
  }
`

const blogPageCSS = css`
  .blog__main {
    .card {
      border-radius: 10px;
    }
    .card-image,
    .gatsby-image-wrapper {
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
    }
    .card-content {
      border-bottom-left-radius: 10px;
      border-bottom-right-radius: 10px;
      border-bottom: 1px solid ${colors.blueHr};
      border-left: 1px solid ${colors.blueHr};
      border-right: 1px solid ${colors.blueHr};
    }
  }
  .blog__sidebar {
    .card {
      border-bottom: 1px solid ${colors.blueHr};
    }
    .card-content {
      padding: 0;
    }
    .media-left {
      border-radius: 10px;
      margin-bottom: 1.5rem;
      margin-top: 1.5rem;
    }
    .gatsby-image-wrapper {
      border-radius: 10px;
    }
    .media-content {
      padding: 1.5rem;
    }
  }
  .blog__sidebar,
  .blog__thumbnail {
    .card {
      box-shadow: none;
    }
  }
  .blog__thumbnail {
    .card-image,
    .gatsby-image-wrapper {
      border-radius: 10px;
    }
  }
`
